import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Plus, Trash, FloppyDisk } from "@phosphor-icons/react";

const TYPES = [["text", "Text"], ["select", "Dropdown"], ["number", "Number"], ["date", "Date"]];

// Admin builder for ClaroFlow custom attributes + attachment size limit.
export default function FlowCustomFieldsDialog({ open, onOpenChange, onSaved }) {
  const [fields, setFields] = useState([]);
  const [maxMb, setMaxMb] = useState(25);
  const [busy, setBusy] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!open) return;
    setReady(false);
    api.get("/flow/custom-fields").then((r) => {
      setFields(r.data.custom_fields || []);
      setMaxMb(r.data.max_attachment_mb || 25);
      setReady(true);
    }).catch(() => setReady(true));
  }, [open]);

  const addField = () => setFields((f) => [...f, { label: "", type: "text", options: [] }]);
  const setField = (i, patch) => setFields((f) => f.map((x, idx) => idx === i ? { ...x, ...patch } : x));
  const removeField = (i) => setFields((f) => f.filter((_, idx) => idx !== i));

  const save = async () => {
    const clean = fields.filter((f) => (f.label || "").trim());
    setBusy(true);
    try {
      await api.put("/flow/custom-fields", { custom_fields: clean });
      await api.put("/flow/settings", { max_attachment_mb: Number(maxMb) || 25 });
      toast.success("Custom fields saved");
      onSaved && onSaved();
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="flow-fields-dialog">
        <DialogHeader><DialogTitle>Custom Attributes &amp; Settings</DialogTitle></DialogHeader>
        {!ready ? (
          <p className="text-sm text-muted-foreground py-6">Loading…</p>
        ) : (
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">Define custom fields shown on every ticket. They become searchable and available as workflow-rule conditions.</p>
          <div className="space-y-2" data-testid="flow-fields-list">
            {fields.length === 0 && <p className="text-xs text-muted-foreground italic">No custom fields yet.</p>}
            {fields.map((f, i) => (
              <div key={i} className="border border-border p-3 space-y-2" data-testid={`flow-field-row-${i}`}>
                <div className="grid grid-cols-[1fr_140px_auto] gap-2 items-end">
                  <div><Label className="text-xs">Field label</Label>
                    <Input data-testid={`flow-field-label-${i}`} value={f.label} onChange={(e) => setField(i, { label: e.target.value })} className="rounded-none mt-1" placeholder="e.g. Vendor" /></div>
                  <div><Label className="text-xs">Type</Label>
                    <Select value={f.type} onValueChange={(v) => setField(i, { type: v })}>
                      <SelectTrigger data-testid={`flow-field-type-${i}`} className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>{TYPES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <button data-testid={`flow-field-remove-${i}`} onClick={() => removeField(i)} className="text-muted-foreground hover:text-red-600 pb-2"><Trash size={16} /></button>
                </div>
                {f.type === "select" && (
                  <div><Label className="text-xs">Options (comma-separated)</Label>
                    <Input value={(f.options || []).join(", ")} onChange={(e) => setField(i, { options: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} className="rounded-none mt-1" placeholder="Low, Medium, High" /></div>
                )}
              </div>
            ))}
          </div>
          <Button data-testid="flow-field-add" variant="outline" size="sm" onClick={addField} className="rounded-none"><Plus size={14} className="mr-1" />Add field</Button>

          <div className="border-t border-border pt-3">
            <Label className="text-xs">Max attachment size (MB)</Label>
            <Input data-testid="flow-max-mb" type="number" min="1" max="200" value={maxMb} onChange={(e) => setMaxMb(e.target.value)} className="rounded-none mt-1 w-32" />
          </div>

          <Button data-testid="flow-fields-save" disabled={busy} onClick={save} className="rounded-none w-full bg-primary hover:bg-primary/90"><FloppyDisk size={16} className="mr-1" />Save</Button>
        </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
